import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import envVars from '../../../constants/envVars';
import useRequest from '../../../hooks/useRequest';
import {
    CmCheckinStatusFilter,
    DisengagedReason,
    MemberSortField,
    MemberSortOrder,
    OrganizationMemberListPaginated
} from '../types';
import { ORGANIZATION_MEMBER_LIST_KEY } from './constants';

export type GetOrganizationMembersParams = {
    search?: string;
    offset?: string;
    limit?: number;
    // Cross-provider search: an array sends repeated ?hero_organization_id= (all accessible orgs)
    // and bypasses the active-org injection in useRequest. Omit for normal single active-org calls.
    hero_organization_id?: string | string[];
    is_online?: boolean;
    device_status?: string;
    state?: string;
    list_version?: string;
    sort_by?: MemberSortField;
    sort_order?: MemberSortOrder;
    cm_checkin_status?: CmCheckinStatusFilter;
    disengaged_reason?: DisengagedReason;
    other_disengaged_reasons?: DisengagedReason[];
    care_manager_ids?: string;
};

const useGetOrganizationMembers = (
    query: GetOrganizationMembersParams,
    options: { enabled?: boolean; staleTime?: number } = {}
) => {
    const req = useRequest();

    const action = useCallback(() => {
        return req<OrganizationMemberListPaginated>(
            query.list_version === 'disengaged'
                ? {
                      url: `${envVars.API_COMMERCE_HOSTNAME}enterprise/disengaged-members`,
                      query: {
                          ...(query.search && { search: query.search }),
                          ...(query.offset && { offset: query.offset }),
                          ...(query.limit && { limit: query.limit }),
                          // [HWM-23296] The one cross-provider key this endpoint takes: an ARRAY
                          // of org ids, which useRequest forwards as repeated params instead of
                          // overriding with the active org. Absent on the list page
                          // (useListQueryParams never sets it), so that path is unchanged.
                          ...(query.hero_organization_id && {
                              hero_organization_id: query.hero_organization_id
                          }),
                          ...(query.sort_by && {
                              sort_by: query.sort_by,
                              sort_order: query.sort_order
                          }),
                          ...(query.disengaged_reason && {
                              disengaged_reason: query.disengaged_reason
                          }),
                          ...(query.care_manager_ids && {
                              care_manager_ids: query.care_manager_ids
                          })
                      }
                  }
                : {
                      url: `${envVars.API_COMMERCE_HOSTNAME}enterprise/members`,
                      query
                  }
        );
    }, [req, query]);

    return useQuery({
        queryFn: action,
        queryKey: [ORGANIZATION_MEMBER_LIST_KEY, query],
        enabled: query.list_version !== 'checkin-members' && options.enabled !== false,
        staleTime: options.staleTime
    });
};

export default useGetOrganizationMembers;
